import Image from "next/image";
import quoteIcon from "@/assets/testimonial/quote.svg";
import { memo, useCallback, useState } from "react";
import { TestimonialItem } from "../home/sections/testimonial/testimonial.type";
import {
  BOY_AVATAR,
  FALLBACK_FEATURE_IMAGE,
  GIRL_AVATAR,
  cn,
  getInitials,
} from "@/lib/utils";
import { isOverflowing } from "@/lib/textOverflow";
import { Typography } from "../Typography";
import { Star } from "lucide-react";

type TestimonialCardProps = {
  item: TestimonialItem;
};

const TestimonialCard = ({ item }: TestimonialCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const avatarSrc =
    item.avatar || (item.gender === "female" ? GIRL_AVATAR : BOY_AVATAR);

  // Ref callback (no useEffect): measure when mounted and on size changes.
  // Skip measuring while expanded, since the clamp is removed.
  const textRef = useCallback(
    (element: HTMLParagraphElement | null) => {
      if (!element || expanded) return;

      const measure = () => setCanExpand(isOverflowing(element));
      measure();

      document.fonts?.ready.then(measure);

      const resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(element);

      return () => resizeObserver.disconnect();
    },
    [expanded, item.content],
  );

  return (
    <article className="flex h-full flex-1 flex-col overflow-hidden rounded-2xl bg-white shadow-product-card border">
      <div className="relative h-[190px] w-full shrink-0 overflow-hidden">
        <Image
          src={item.feature || FALLBACK_FEATURE_IMAGE}
          alt={item.name}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          className="object-cover rounded-2xl"
        />
      </div>

      <div className="flex flex-1 flex-col justify-between gap-4 p-4">
        <div className="flex items-start gap-3 min-h-35">
          <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
            <Image
              src={quoteIcon}
              alt=""
              width={24}
              height={24}
              className="size-6 shrink-0"
            />
            <div className="min-w-0 w-full">
              <Typography variant="body2">
                <p
                  ref={textRef}
                  className={cn("", !expanded && "line-clamp-6")}
                >
                  {item.content}
                </p>
              </Typography>
              {canExpand ? (
                <button
                  type="button"
                  onClick={() => setExpanded((prev) => !prev)}
                  className="mt-1 text-[12px] leading-4 text-secondary hover:underline"
                >
                  {expanded ? "Show less" : "Read more"}
                </button>
              ) : null}
            </div>
          </div>

          {item.reference ? (
            <div className="relative self-stretch h-38 w-18 shrink-0 overflow-hidden rounded-xl border bg-subtle">
              <Image
                src={item.reference}
                alt={`${item.name} reference`}
                fill
                sizes="64px"
                className="object-cover object-top"
              />
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative size-10 shrink-0 overflow-hidden rounded-full bg-subtle">
            {avatarSrc ? (
              <Image
                src={avatarSrc}
                alt={item.name}
                width={40}
                height={40}
                className="size-full object-cover"
              />
            ) : (
              <div className="flex size-full items-center justify-center text-[13px] font-semibold text-body">
                {getInitials(item.name)}
              </div>
            )}
          </div>
          <div className="min-w-0">
            <Typography variant="subtitle1" className="truncate font-semibold">
              {item.name}
            </Typography>
            <div
              className="flex gap-0.5"
              role="img"
              aria-label={`${item.rating} out of 5 stars`}
            >
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className={cn(
                    "size-3.5",
                    i < item.rating
                      ? "fill-yellow-400 text-yellow-400"
                      : "fill-transparent text-caption",
                  )}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export default memo(TestimonialCard);
